import JSZip from 'jszip';
import { useEffect, useRef, useState, useCallback } from 'react';


const EMU_PX = 9525;
const emu2px = (emu) => Math.round(parseInt(emu || 0) / EMU_PX);

const ZOOM_STEP = 0.1;
const MIN_ZOOM  = 0.5;
const MAX_ZOOM  = 3;

// ════════════════════════════════════════════════════════════
//  PPTX ENGINE
// ════════════════════════════════════════════════════════════

function resolveColor(xml, theme = {}) {
  const solid = xml.match(/<(?:[a-z]+:)?solidFill>([\s\S]*?)<\/(?:[a-z]+:)?solidFill>/);
  if (solid) {
    const s   = solid[1].match(/srgbClr val="([0-9a-fA-F]+)"/);
    if (s) return '#' + s[1];
    const sys = solid[1].match(/sysClr[^>]*lastClr="([0-9a-fA-F]+)"/);
    if (sys) return '#' + sys[1];
    const sc  = solid[1].match(/schemeClr val="([^"]+)"/);
    if (sc && theme[sc[1]]) return theme[sc[1]];
  }
  const grad = xml.match(/<(?:[a-z]+:)?gradFill>([\s\S]*?)<\/(?:[a-z]+:)?gradFill>/);
  if (grad) {
    const stops = [];
    const stopRe = /<a:gs[^>]*pos="(\d+)"[^>]*>([\s\S]*?)<\/a:gs>/g;
    let sm;
    while ((sm = stopRe.exec(grad[1])) !== null) {
      const pct = parseInt(sm[1]) / 1000;
      const sc  = sm[2].match(/srgbClr val="([0-9a-fA-F]+)"/);
      const sys = sm[2].match(/sysClr[^>]*lastClr="([0-9a-fA-F]+)"/);
      const color = sc ? '#' + sc[1] : sys ? '#' + sys[1] : null;
      if (color) stops.push({ pct, color });
    }
    if (stops.length >= 2) {
      const ang = grad[1].match(/lin[^>]*ang="(\d+)"/);
      const deg = ang ? Math.round(parseInt(ang[1]) / 60000) : 90;
      return `linear-gradient(${deg}deg,${stops.map(s => `${s.color} ${s.pct}%`).join(',')})`;
    }
    if (stops.length === 1) return stops[0].color;
  }
  return null;
}

async function parseTheme(zip) {
  const colors = {};
  try {
    const tf = Object.keys(zip.files).find(f => /ppt\/theme\/theme\d+\.xml/.test(f));
    if (!tf) return colors;
    const xml = await zip.file(tf).async('string');
    ['dk1','lt1','dk2','lt2','accent1','accent2','accent3','accent4','accent5','accent6','hlink'].forEach(k => {
      const re = new RegExp(`<a:${k}>[\\s\\S]*?(?:srgbClr|sysClr)[^>]*(?:val|lastClr)="([0-9a-fA-F]+)"`);
      const m  = xml.match(re);
      if (m) colors[k] = '#' + m[1];
    });
  } catch {}
  return colors;
}

async function parseRels(zip, path) {
  const rels = {};
  try {
    if (!zip.file(path)) return rels;
    const xml = await zip.file(path).async('string');
    const re  = /<Relationship[^>]*Id="([^"]*)"[^>]*Type="([^"]*)"[^>]*Target="([^"]*)"/g;
    let m;
    while ((m = re.exec(xml)) !== null) rels[m[1]] = { type: m[2], target: m[3] };
  } catch {}
  return rels;
}

async function resolveImage(zip, relsPath, rId) {
  try {
    const rels = await parseRels(zip, relsPath);
    const rel  = rels[rId];
    if (!rel) return null;
    const norm = ('ppt/slides/' + rel.target).replace(/\/slides\/\.\.\//, '/');
    const file = zip.file(norm);
    if (!file) return null;
    const b64  = await file.async('base64');
    const ext  = norm.split('.').pop().toLowerCase();
    const mime = { png:'image/png', jpg:'image/jpeg', jpeg:'image/jpeg', gif:'image/gif', svg:'image/svg+xml' }[ext] || 'image/png';
    return `data:${mime};base64,${b64}`;
  } catch { return null; }
}

function textRuns(paraXml, theme, defColor = '#000') {
  const runs = [];
  const re   = /<a:r>([\s\S]*?)<\/a:r>/g;
  let m;
  while ((m = re.exec(paraXml)) !== null) {
    const rx   = m[1];
    const t    = rx.match(/<a:t>([^<]*)<\/a:t>/);
    if (!t) continue;
    const text = t[1].replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&quot;/g,'"').replace(/&apos;/g,"'");
    const rp   = rx.match(/<a:rPr([^>]*)>/);
    const rpa  = rp ? rp[1] : '';
    const bold   = /b="1"|b="true"/.test(rpa);
    const italic = /i="1"|i="true"/.test(rpa);
    const uline  = /u="sng"|u="dbl"/.test(rpa);
    const szM    = rpa.match(/sz="(\d+)"/);
    const sz     = szM ? parseInt(szM[1]) / 100 : null;
    const color  = resolveColor(rx, theme) || defColor;
    const fontM  = rx.match(/typeface="([^+][^"]*)"/);
    const font   = fontM ? fontM[1] : null;
    runs.push({ text, bold, italic, uline, sz, color, font });
  }
  const fr = /<a:fld[^>]*>([\s\S]*?)<\/a:fld>/g;
  while ((m = fr.exec(paraXml)) !== null) {
    const t = m[1].match(/<a:t>([^<]*)<\/a:t>/);
    if (t) runs.push({ text:t[1], bold:false, italic:false, uline:false, sz:null, color:defColor, font:null });
  }
  return runs;
}

function renderTextBody(bodyXml, theme) {
  const bpM    = bodyXml.match(/<a:bodyPr([^>]*)>/);
  const bpa    = bpM ? bpM[1] : '';
  const anchor = (bpa.match(/anchor="([^"]+)"/) || [])[1] || 'ctr';
  const vAlign = anchor === 't' ? 'flex-start' : anchor === 'b' ? 'flex-end' : 'center';
  const lIns   = bpa.match(/lIns="(\d+)"/) ? emu2px(bpa.match(/lIns="(\d+)"/)[1]) : 7;
  const rIns   = bpa.match(/rIns="(\d+)"/) ? emu2px(bpa.match(/rIns="(\d+)"/)[1]) : 7;
  const tIns   = bpa.match(/tIns="(\d+)"/) ? emu2px(bpa.match(/tIns="(\d+)"/)[1]) : 4;
  const bIns   = bpa.match(/bIns="(\d+)"/) ? emu2px(bpa.match(/bIns="(\d+)"/)[1]) : 4;
  const noWrap = /\bnoWrap\b/.test(bodyXml);

  let html = `<div style="display:flex;flex-direction:column;justify-content:${vAlign};height:100%;padding:${tIns}px ${rIns}px ${bIns}px ${lIns}px;box-sizing:border-box;white-space:${noWrap?'nowrap':'normal'};">`;

  const paraRe = /<a:p>([\s\S]*?)<\/a:p>/g;
  let pm;
  while ((pm = paraRe.exec(bodyXml)) !== null) {
    const px   = pm[1];
    const ppM  = px.match(/<a:pPr([^>]*)>/);
    const ppa  = ppM ? ppM[1] : '';
    const algn = (ppa.match(/algn="([^"]+)"/) || [])[1] || 'l';
    const cssA = { l:'left', r:'right', ctr:'center', just:'justify', dist:'justify' }[algn] || 'left';
    const marL = ppa.match(/marL="(\d+)"/) ? emu2px(ppa.match(/marL="(\d+)"/)[1]) : 0;
    const spcB = px.match(/spcBef[\s\S]*?spcPts[^>]*val="(\d+)"/) ? parseInt(px.match(/spcBef[\s\S]*?spcPts[^>]*val="(\d+)"/)[1]) / 100 : 0;
    const spcA = px.match(/spcAft[\s\S]*?spcPts[^>]*val="(\d+)"/) ? parseInt(px.match(/spcAft[\s\S]*?spcPts[^>]*val="(\d+)"/)[1]) / 100 : 0;
    const lhM  = px.match(/lnSpc[\s\S]*?spcPct[^>]*val="(\d+)"/);
    const lh   = lhM ? parseInt(lhM[1]) / 100000 : 1.2;

    const runs = textRuns(px, theme);
    if (!runs.length) { html += `<p style="margin:0;padding:${spcB}px 0 ${spcA}px;min-height:1em;">&nbsp;</p>`; continue; }

    html += `<p style="margin:0;padding:${spcB}px 0 ${spcA}px;text-align:${cssA};line-height:${lh};padding-left:${marL}px;">`;
    runs.forEach(r => {
      const fs = r.sz || runs.find(x => x.sz)?.sz || 16;
      html += `<span style="font-size:${fs}px;color:${r.color};font-weight:${r.bold?'bold':'normal'};font-style:${r.italic?'italic':'normal'};${r.uline?'text-decoration:underline;':''}${r.font?`font-family:"${r.font}",sans-serif;`:''}">${r.text.replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/ /g,'\u00a0')}</span>`;
    });
    html += '</p>';
  }
  html += '</div>';
  return html;
}

function getXfrm(xml) {
  const xm = xml.match(/<(?:a:|p:)?xfrm([^>]*)>([\s\S]*?)<\/(?:a:|p:)?xfrm>/);
  if (!xm) return null;
  const inner = xm[2];
  const off   = inner.match(/<a:off[^>]*x="([^"]*)"[^>]*y="([^"]*)"/);
  const ext   = inner.match(/<a:ext[^>]*cx="([^"]*)"[^>]*cy="([^"]*)"/);
  if (!off || !ext) return null;
  const rotM = xm[1].match(/rot="(-?\d+)"/);
  return {
    x: emu2px(off[1]), y: emu2px(off[2]),
    cx: emu2px(ext[1]), cy: emu2px(ext[2]),
    rot: rotM ? parseInt(rotM[1]) / 60000 : 0,
    flipH: /flipH="1"/.test(xm[1]),
    flipV: /flipV="1"/.test(xm[1]),
  };
}

function renderSp(spXml, theme) {
  const xf = getXfrm(spXml);
  if (!xf) return '';
  const { x, y, cx, cy, rot, flipH, flipV } = xf;
  const bg   = resolveColor(spXml, theme);
  const geom = (spXml.match(/prst="([^"]+)"/) || [])[1] || 'rect';

  const lnM = spXml.match(/<a:ln[^>]*w="(\d+)"[^>]*>([\s\S]*?)<\/a:ln>/);
  let border = '';
  if (lnM && !/<a:noFill\/>/.test(lnM[2])) {
    const bw = Math.max(1, Math.round(parseInt(lnM[1]) / 12700));
    const bc = resolveColor(lnM[2], theme) || '#333';
    border = `border:${bw}px solid ${bc};`;
  }

  const transforms = [];
  if (rot)   transforms.push(`rotate(${rot}deg)`);
  if (flipH) transforms.push('scaleX(-1)');
  if (flipV) transforms.push('scaleY(-1)');

  if (geom === 'triangle') {
    return `<div style="position:absolute;left:${x}px;top:${y}px;width:${cx}px;height:${cy}px;${bg?`background:${bg};`:''}clip-path:polygon(50% 0%,100% 100%,0% 100%);${transforms.length?`transform:${transforms.join(' ')};`:''}"></div>`;
  }

  let radius = '';
  if (geom === 'ellipse' || geom === 'circle') radius = 'border-radius:50%;';
  else if (geom === 'roundRect') radius = 'border-radius:8px;';

  const bgStyle = bg ? `background:${bg};` : '';
  const txBody  = spXml.match(/<p:txBody>([\s\S]*?)<\/p:txBody>/);

  let html = `<div style="position:absolute;left:${x}px;top:${y}px;width:${cx}px;height:${cy}px;${bgStyle}${border}${radius}overflow:visible;box-sizing:border-box;${transforms.length?`transform:${transforms.join(' ')};`:''}">`;
  if (txBody) html += renderTextBody(txBody[1], theme);
  html += '</div>';
  return html;
}

async function renderPic(picXml, zip, relsPath) {
  const xf   = getXfrm(picXml);
  if (!xf) return '';
  const { x, y, cx, cy, rot } = xf;
  const rIdM = picXml.match(/r:embed="([^"]+)"/);
  if (!rIdM) return '';
  const src  = await resolveImage(zip, relsPath, rIdM[1]);
  if (!src) return '';
  return `<div style="position:absolute;left:${x}px;top:${y}px;width:${cx}px;height:${cy}px;${rot?`transform:rotate(${rot}deg);`:''}overflow:hidden;"><img src="${src}" style="width:100%;height:100%;object-fit:fill;" /></div>`;
}

function renderTbl(tblXml, xfrmXml, theme) {
  const xf = getXfrm(`<p:xfrm>${xfrmXml}</p:xfrm>`);
  if (!xf) return '';
  const { x, y, cx } = xf;

  const colWidths = [];
  const cre = /<a:gridCol[^>]*w="(\d+)"/g;
  let cm;
  while ((cm = cre.exec(tblXml)) !== null) colWidths.push(emu2px(cm[1]));

  // FIX: height:auto so all rows are visible, overflow:visible
  let html = `<div style="position:absolute;left:${x}px;top:${y}px;width:${cx}px;height:auto;overflow:visible;"><table style="border-collapse:collapse;width:100%;table-layout:fixed;font-size:12px;"><colgroup>`;
  colWidths.forEach(w => html += `<col style="width:${w}px;">`);
  html += '</colgroup>';

  const rowRe = /<a:tr[^>]*h="(\d+)"[^>]*>([\s\S]*?)<\/a:tr>/g;
  let rm;
  while ((rm = rowRe.exec(tblXml)) !== null) {
    const rh = emu2px(rm[1]);
    html += `<tr style="height:${rh}px;">`;
    const cellRe = /<a:tc>([\s\S]*?)<\/a:tc>/g;
    let c;
    while ((c = cellRe.exec(rm[2])) !== null) {
      const cx2 = c[1];
      if (/<a:hMerge\/>/.test(cx2) || /<a:vMerge\/>/.test(cx2)) continue;
      const bg2     = resolveColor(cx2, theme) || 'transparent';
      const gsM     = cx2.match(/gridSpan="(\d+)"/);
      const rsM     = cx2.match(/rowSpan="(\d+)"/);
      const colspan = gsM ? ` colspan="${gsM[1]}"` : '';
      const rowspan = rsM ? ` rowspan="${rsM[1]}"` : '';

      const lnB = cx2.match(/<a:lnB[^>]*w="(\d+)"[^>]*>([\s\S]*?)<\/a:lnB>/);
      const lnR = cx2.match(/<a:lnR[^>]*w="(\d+)"[^>]*>([\s\S]*?)<\/a:lnR>/);
      const lnT = cx2.match(/<a:lnT[^>]*w="(\d+)"[^>]*>([\s\S]*?)<\/a:lnT>/);
      const lnL = cx2.match(/<a:lnL[^>]*w="(\d+)"[^>]*>([\s\S]*?)<\/a:lnL>/);
      const bord = (ln, side) => ln && !/<a:noFill\/>/.test(ln[2])
        ? `border-${side}:${Math.max(1, parseInt(ln[1]) / 12700)}px solid ${resolveColor(ln[2], theme) || '#ccc'};`
        : '';
      const borderStyle = bord(lnT,'top') + bord(lnB,'bottom') + bord(lnL,'left') + bord(lnR,'right');

      let cellHtml = '';
      const tb = cx2.match(/<a:txBody>([\s\S]*?)<\/a:txBody>/);
      if (tb) {
        const pr = /<a:p>([\s\S]*?)<\/a:p>/g;
        let pp;
        while ((pp = pr.exec(tb[1])) !== null) {
          const runs = textRuns(pp[1], theme);
          if (!runs.length) { cellHtml += '<br>'; continue; }
          const ppM  = pp[1].match(/<a:pPr([^>]*)/);
          const algM = ppM ? ppM[1].match(/algn="([^"]+)"/) : null;
          const algn = algM ? ({ l:'left',r:'right',ctr:'center',just:'justify' }[algM[1]] || 'left') : 'left';
          cellHtml += `<p style="margin:0;text-align:${algn};">`;
          runs.forEach(r => {
            cellHtml += `<span style="font-size:${r.sz||11}px;color:${r.color};font-weight:${r.bold?'bold':'normal'};font-style:${r.italic?'italic':'normal'};">${r.text.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</span>`;
          });
          cellHtml += '</p>';
        }
      }
      html += `<td${colspan}${rowspan} style="background:${bg2};${borderStyle}padding:3px 5px;vertical-align:middle;">${cellHtml}</td>`;
    }
    html += '</tr>';
  }
  html += '</table></div>';
  return html;
}

function renderCxn(cxnXml, theme) {
  const xf = getXfrm(cxnXml);
  if (!xf) return '';
  const { x, y, cx, cy } = xf;
  const color = resolveColor(cxnXml, theme) || '#555';
  const angle = Math.atan2(cy, cx) * 180 / Math.PI;
  const len   = Math.sqrt(cx * cx + cy * cy);
  const lnM  = cxnXml.match(/<a:ln[^>]*w="(\d+)"/);
  const lw   = lnM ? Math.max(1, Math.round(parseInt(lnM[1]) / 12700)) : 1;
  return `<div style="position:absolute;left:${x}px;top:${y}px;width:${len}px;height:${lw}px;background:${color};transform-origin:0 50%;transform:rotate(${angle}deg);"></div>`;
}

async function getBackground(zip, slideXml, slideRels, theme) {
  const bgM = slideXml.match(/<p:bg>([\s\S]*?)<\/p:bg>/);
  if (bgM) {
    const blipM = bgM[1].match(/r:embed="([^"]+)"/);
    if (blipM) {
      const src = await resolveImage(zip, slideRels.__path || '', blipM[1]);
      if (src) return { type:'image', value:src };
    }
    const color = resolveColor(bgM[1], theme);
    if (color) return { type:'color', value:color };
  }
  const layoutRel = Object.values(slideRels).find(r => r.type?.includes('slideLayout'));
  if (layoutRel) {
    try {
      const lxml = await zip.file('ppt/' + layoutRel.target.replace('../', '')).async('string');
      const lbg  = lxml.match(/<p:bg>([\s\S]*?)<\/p:bg>/);
      if (lbg) {
        const color = resolveColor(lbg[1], theme);
        if (color) return { type:'color', value:color };
      }
    } catch {}
  }
  return { type:'color', value:'#FFFFFF' };
}

async function buildSlideHtml(zip, slideIndex, theme) {
  const slideFiles = Object.keys(zip.files)
    .filter(f => /^ppt\/slides\/slide\d+\.xml$/.test(f))
    .sort((a, b) => parseInt(a.match(/slide(\d+)/)[1]) - parseInt(b.match(/slide(\d+)/)[1]));

  if (slideIndex >= slideFiles.length) return null;

  const slidePath = slideFiles[slideIndex];
  const slideXml  = await zip.file(slidePath).async('string');
  const slideNum  = parseInt(slidePath.match(/slide(\d+)/)[1]);
  const relsPath  = `ppt/slides/_rels/slide${slideNum}.xml.rels`;
  const slideRels = await parseRels(zip, relsPath);
  slideRels.__path = relsPath;

  const presXml = await zip.file('ppt/presentation.xml').async('string');
  const szM     = presXml.match(/p:sldSz[^>]*cx="(\d+)"[^>]*cy="(\d+)"/);
  const slideW  = szM ? emu2px(szM[1]) : 960;
  const slideH  = szM ? emu2px(szM[2]) : 540;

  const bg      = await getBackground(zip, slideXml, slideRels, theme);
  const bgStyle = bg.type === 'color' ? `background:${bg.value};` : 'background:#fff;';

  // KEY FIX: position relative + min-height (not fixed height) + NO overflow:hidden
  // This lets absolutely-positioned children that go below slideH still be visible
  let html = `<div style="position:relative;width:${slideW}px;min-height:${slideH}px;${bgStyle}">`;

  // Background image pinned to slideH
  if (bg.type === 'image') {
    html += `<img src="${bg.value}" style="position:absolute;top:0;left:0;width:100%;height:${slideH}px;object-fit:cover;z-index:0;" />`;
  }

  // This invisible spacer ensures the container is at least slideH tall
  // so absolute children inside slideH bounds stay in correct position
  html += `<div style="height:${slideH}px;width:1px;"></div>`;

  const spTree = slideXml.match(/<p:spTree>([\s\S]*?)<\/p:spTree>/);
  if (spTree) {
    const tree = spTree[1];
    let m;

    const picRe = /<p:pic>([\s\S]*?)<\/p:pic>/g;
    while ((m = picRe.exec(tree)) !== null) html += await renderPic(m[1], zip, relsPath);

    const gfRe = /<p:graphicFrame>([\s\S]*?)<\/p:graphicFrame>/g;
    while ((m = gfRe.exec(tree)) !== null) {
      const gfXml = m[1];
      const xfrmM = gfXml.match(/<p:xfrm>([\s\S]*?)<\/p:xfrm>/);
      const tblM  = gfXml.match(/<a:tbl>([\s\S]*?)<\/a:tbl>/);
      if (xfrmM && tblM) html += renderTbl(tblM[1], xfrmM[1], theme);
    }

    const spRe = /<p:sp>([\s\S]*?)<\/p:sp>/g;
    while ((m = spRe.exec(tree)) !== null) html += renderSp(m[1], theme);

    const cxnRe = /<p:cxnSp>([\s\S]*?)<\/p:cxnSp>/g;
    while ((m = cxnRe.exec(tree)) !== null) html += renderCxn(m[1], theme);
  }

  html += '</div>';
  return { html, slideW, slideH };
}

// ════════════════════════════════════════════════════════════
//  COMPONENT
// ════════════════════════════════════════════════════════════

export const PptViewer = ({ url }) => {
  const containerRef = useRef(null);
  const slideBoxRef  = useRef(null);
  const zipRef       = useRef(null);
  const themeRef     = useRef({});

  const [numSlides, setNumSlides] = useState(0);
  const [page,      setPage]      = useState(1);
  const [inputPage, setInputPage] = useState('1');
  const [zoom,      setZoom]      = useState(1);
  const [slideW,    setSlideW]    = useState(960);
  const [slideH,    setSlideH]    = useState(540);
  const [baseScale, setBaseScale] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error,     setError]     = useState(null);

  const renderPage = useCallback(async (zip, theme, pageNum) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await buildSlideHtml(zip, pageNum - 1, theme);
      if (!result) throw new Error('Slide not found');
      const { html, slideW: sw, slideH: sh } = result;

      if (slideBoxRef.current) {
        slideBoxRef.current.innerHTML = html;
        // Wait for paint then measure actual rendered size
        await new Promise(r => requestAnimationFrame(r));
        const child   = slideBoxRef.current.firstElementChild;
        const actualH = child ? child.scrollHeight : sh;
        const actualW = child ? child.scrollWidth  : sw;
        setSlideW(Math.max(sw, actualW));
        setSlideH(Math.max(sh, actualH));
      }
      setIsLoading(false);
    } catch (e) {
      setError(e.message || 'Render failed');
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;

        const zip   = await JSZip.loadAsync(buf);
        const theme = await parseTheme(zip);
        zipRef.current   = zip;
        themeRef.current = theme;

        const slides = Object.keys(zip.files).filter(f => /^ppt\/slides\/slide\d+\.xml$/.test(f));
        if (cancelled) return;

        setNumSlides(slides.length || 1);
        setPage(1);
        setInputPage('1');
        await renderPage(zip, theme, 1);
      } catch (e) {
        if (!cancelled) { setError(e.message || 'Load failed'); setIsLoading(false); }
      }
    })();

    return () => { cancelled = true; };
  }, [url, renderPage]);

  useEffect(() => {
    if (zipRef.current && page > 0 && numSlides > 0)
      renderPage(zipRef.current, themeRef.current, page);
  }, [page, numSlides, renderPage]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const { width, height } = el.getBoundingClientRect();
      if (!width || !slideW) return;
      setBaseScale(Math.min((width - 32) / slideW, (height - 32) / slideH));
    };
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    compute();
    return () => ro.disconnect();
  }, [slideW, slideH]);

  const goTo = (n) => {
    const c = Math.min(Math.max(1, n), numSlides || 1);
    setPage(c); setInputPage(String(c));
  };

  const effectiveScale = baseScale * zoom;
  const scaledW = Math.floor(slideW * effectiveScale);
  const scaledH = Math.floor(slideH * effectiveScale);

  return (
    <div className="flex flex-col w-full h-full bg-gray-200">

      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 text-white text-sm shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={() => goTo(page - 1)} disabled={page <= 1}
            className="px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-40 text-lg leading-none">‹</button>
          <div className="flex items-center gap-1">
            <input type="text" value={inputPage}
              onChange={e => setInputPage(e.target.value)}
              onBlur={() => { const n = parseInt(inputPage,10); if (!isNaN(n)) goTo(n); else setInputPage(String(page)); }}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
              className="w-10 text-center bg-gray-700 rounded px-1 py-0.5 text-white" />
            <span className="text-gray-400">/ {numSlides || '...'}</span>
          </div>
          <button onClick={() => goTo(page + 1)} disabled={page >= numSlides}
            className="px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-40 text-lg leading-none">›</button>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))} disabled={zoom <= MIN_ZOOM}
            className="px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-40 text-lg leading-none">−</button>
          <span className="w-14 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))} disabled={zoom >= MAX_ZOOM}
            className="px-2 py-1 rounded hover:bg-gray-600 disabled:opacity-40 text-lg leading-none">+</button>
          <button onClick={() => setZoom(1)}
            className="px-2 py-1 rounded hover:bg-gray-600 text-xs text-gray-300">Reset</button>
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-200">
        <div style={{ minWidth:`${scaledW + 32}px`, minHeight:`${scaledH + 32}px` }}
          className="flex justify-center items-start py-4">

          <div style={{ position:'relative', width:scaledW, height:scaledH, flexShrink:0 }}>

            {/* Skeleton */}
            {isLoading && (
              <div style={{ position:'absolute', inset:0, zIndex:2, background:'#fff',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 10px 25px -5px rgba(0,0,0,0.15)' }}>
                <div style={{ width:'70%' }}>
                  {[55,100,80,65].map((w,i) => (
                    <div key={i} style={{ height:i===0?20:12, borderRadius:4, background:'#e5e7eb',
                      marginBottom:12, width:`${w}%`,
                      animation:`pulse 1.5s ease-in-out ${i*0.15}s infinite` }} />
                  ))}
                </div>
                <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
              </div>
            )}

            {error && !isLoading && (
              <div style={{ position:'absolute', inset:0, background:'#fff',
                display:'flex', alignItems:'center', justifyContent:'center',
                color:'#ef4444', fontSize:14, padding:24, textAlign:'center' }}>
                ⚠️ {error}
              </div>
            )}

            {/* No overflow:hidden — content must never be clipped */}
            <div style={{
              transform:`scale(${effectiveScale})`,
              transformOrigin:'top left',
              width:slideW,
              height:slideH,
              opacity:isLoading ? 0 : 1,
              boxShadow:'0 10px 25px -5px rgba(0,0,0,0.15)',
              background:'#fff',
            }}>
              <div ref={slideBoxRef} style={{ width:'100%', height:'100%', fontFamily:'Calibri,Arial,sans-serif' }} />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};